import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import './risk-overview.css';
import './risk-register.css';
import { RiskPlusPageShell, RiskPlusPagerNav, RiskPlusPagerLink, riskColor, riskLabel } from './RiskPlusPageShell';
import { useRiskPlusPageData } from './useRiskPlusPageData';
import { harmCategoryToLabel, accountableOwnerToLabel, type THarmCategory } from '../../entities/risk/Harm';
import type { RiskPlusReport } from '../../entities/report/RiskPlusReport';
import type { RiskRegistryItem } from '../../entities/report/RiskRegistry';

const CATEGORY_ORDER: THarmCategory[] = ['legal_regulatory', 'operational', 'reputational', 'financial'];

const CATEGORY_ICON: Record<THarmCategory, string> = {
  legal_regulatory: 'gavel',
  operational: 'settings',
  reputational: 'reviews',
  financial: 'payments',
};

function RiskRankChip({ rank }: { rank: RiskRegistryItem['riskRank'] }) {
  return (
    <span className="rovw-sev-chip">
      <span className="rovw-sev-dot" style={{ background: riskColor(rank) }} />
      {riskLabel(rank)}
    </span>
  );
}

function CategoryChip({ category }: { category: THarmCategory }) {
  return (
    <span className="rreg-cat-chip">
      <span className="aq-icon" aria-hidden>{CATEGORY_ICON[category] ?? 'label'}</span>
      {harmCategoryToLabel(category)}
    </span>
  );
}

function RiskRegisterSecondaryNav({ useCaseChatId }: { useCaseChatId: string }) {
  return (
    <RiskPlusPagerNav>
      <RiskPlusPagerLink
        to={`/use-case/${encodeURIComponent(useCaseChatId)}/deployment-risk-profile`}
        label="Risk Domains"
        direction="prev"
      />
      <RiskPlusPagerLink
        to={`/use-case/${encodeURIComponent(useCaseChatId)}/risk-controls`}
        label="Required Controls"
        direction="next"
      />
    </RiskPlusPagerNav>
  );
}

function RiskRegisterHeader() {
  return (
    <div className="rovw-hdr">
      <div className="rovw-hdr-icon">
        <span className="aq-icon" aria-hidden>checklist</span>
      </div>
      <div className="rovw-hdr-main">
        <h2 className="rovw-hdr-title">Risk Register</h2>
      </div>
    </div>
  );
}

function FilterTabs({
  selected,
  onChange,
  counts,
  total,
}: {
  selected: 'all' | THarmCategory;
  onChange: (v: 'all' | THarmCategory) => void;
  counts: Record<THarmCategory, number>;
  total: number;
}) {
  const categories = CATEGORY_ORDER.filter((c) => counts[c] > 0);

  return (
    <div className="rreg-tabs">
      <button
        type="button"
        className={`rreg-tab ${selected === 'all' ? 'rreg-tab-active' : ''}`}
        onClick={() => onChange('all')}
      >
        <span className="aq-icon" aria-hidden>list</span>
        All
        <span className="rreg-tab-count">{total}</span>
      </button>
      {categories.map((cat) => (
        <button
          key={cat}
          type="button"
          className={`rreg-tab ${selected === cat ? 'rreg-tab-active' : ''}`}
          onClick={() => onChange(cat)}
        >
          <span className="aq-icon" aria-hidden>{CATEGORY_ICON[cat] ?? 'label'}</span>
          {harmCategoryToLabel(cat)}
          <span className="rreg-tab-count">{counts[cat]}</span>
        </button>
      ))}
    </div>
  );
}

function RiskRow({
  risk,
  index,
  active,
  onOpen,
}: {
  risk: RiskRegistryItem;
  index: number;
  active: boolean;
  onOpen: () => void;
}) {
  return (
    <button
      type="button"
      className={`rreg-row ${active ? 'rreg-row-active' : ''}`}
      onClick={onOpen}
    >
      <div className="rreg-row-main">
        <div className="rreg-row-title">{risk.label || `Risk ${index + 1}`}</div>
        {risk.riskOwner && (
          <div className="rreg-row-sub">
            <span className="aq-icon" aria-hidden>account_circle</span>
            {risk.riskOwner}
          </div>
        )}
      </div>
      <div className="rreg-row-right">
        <CategoryChip category={risk.harmCategory} />
        <RiskRankChip rank={risk.riskRank} />
        <span className="aq-icon rreg-row-chev" aria-hidden>chevron_right</span>
      </div>
    </button>
  );
}

function RiskDetailDrawer({ risk, onClose }: { risk: RiskRegistryItem; onClose: () => void }) {
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return (
    <div className="rovw-scrim" onClick={onClose}>
      <div className="rovw-drawer" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="rovw-drawer-hdr">
          <div className="rovw-drawer-hdr-top">
            <div className="rovw-drawer-hdr-ic">
              <span className="aq-icon" aria-hidden>checklist</span>
            </div>
            <div className="rovw-drawer-title">{risk.label}</div>
            <button type="button" className="rovw-drawer-close" onClick={onClose} aria-label="Close">
              <span className="aq-icon" aria-hidden>close</span>
            </button>
          </div>
          <div className="rreg-drawer-chips">
            <RiskRankChip rank={risk.riskRank} />
            <CategoryChip category={risk.harmCategory} />
          </div>
          {risk.lifeCycleStage && (
            <div className="rreg-lifecycle">
              <span className="rreg-axis-label">Lifecycle stage</span>
              <span className="rovw-stage-pill">{risk.lifeCycleStage}</span>
            </div>
          )}
          {risk.riskOwner && (
            <div className="rreg-owner-row">
              <span className="aq-icon" aria-hidden>account_circle</span>
              {risk.riskOwner}
            </div>
          )}
        </div>

        <div className="rovw-drawer-body">
          {risk.riskScenarioDescription && (
            <div className="rovw-dsec">
              <div className="rovw-dsec-label">Risk Scenario</div>
              <div className="rdom-dsec-text">{risk.riskScenarioDescription}</div>
            </div>
          )}

          {risk.causalHazards.length > 0 && (
            <div className="rovw-dsec">
              <div className="rovw-dsec-label">Direct Causes</div>
              {risk.causalHazards.map((h, i) => (
                <div key={i} className="rreg-sub-block">
                  {h.label && <div className="rreg-sub-label">{h.label}</div>}
                  {h.description && <div className="rreg-sub-desc">{h.description}</div>}
                </div>
              ))}
            </div>
          )}

          {risk.enablingFactors.length > 0 && (
            <div className="rovw-dsec">
              <div className="rovw-dsec-label">Enablers and Amplifiers</div>
              {risk.enablingFactors.map((f, i) => (
                <div key={i} className="rreg-sub-block">
                  {f.label && <div className="rreg-sub-label">{f.label}</div>}
                  {f.description && <div className="rreg-sub-desc">{f.description}</div>}
                </div>
              ))}
            </div>
          )}

          {risk.deployerImpact.length > 0 && (
            <div className="rovw-dsec">
              <div className="rovw-dsec-label">Deployer Impact</div>
              {risk.deployerImpact.map((d, i) => (
                <div key={i} className="rreg-sub-block">
                  <div className="rreg-sub-row">
                    {(d.label || d.description) && (
                      <div className="rreg-sub-label">{d.label || d.description}</div>
                    )}
                    {d.impactArea && <span className="rreg-tag">{d.impactArea}</span>}
                  </div>
                  {d.description && d.label && d.description !== d.label && (
                    <div className="rreg-sub-desc">{d.description}</div>
                  )}
                </div>
              ))}
            </div>
          )}

          {risk.mitigationRequirements.length > 0 && (
            <div className="rovw-dsec">
              <div className="rovw-dsec-label">Required Controls</div>
              {risk.mitigationRequirements.map((req, i) => (
                <div key={i} className="rreg-sub-block">
                  <div className="rreg-sub-label">{req.label || req.description}</div>
                  {req.description && req.label && req.description !== req.label && (
                    <div className="rreg-sub-desc">{req.description}</div>
                  )}
                  {req.accountableOwner && (
                    <span className="rreg-tag-owner">
                      <span className="aq-icon" aria-hidden>badge</span>
                      {accountableOwnerToLabel(req.accountableOwner)}
                    </span>
                  )}
                  {req.conditionsOfAcceptability && (
                    <div className="rreg-accept-line">
                      <span className="rreg-accept-label">Acceptable when</span> {req.conditionsOfAcceptability}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function RiskRegisterContent({ report }: { report: RiskPlusReport }) {
  const [filter, setFilter] = useState<'all' | THarmCategory>('all');
  const [openId, setOpenId] = useState<string | null>(null);

  const counts = CATEGORY_ORDER.reduce<Record<THarmCategory, number>>((acc, c) => {
    acc[c] = report.riskRegister.filter((x) => x.harmCategory === c).length;
    return acc;
  }, {} as Record<THarmCategory, number>);

  const filtered = filter === 'all'
    ? report.riskRegister
    : report.riskRegister.filter((r) => r.harmCategory === filter);

  const openRisk = report.riskRegister.find((r, i) => (r.id || `idx-${i}`) === openId) ?? null;

  return (
    <div className="rovw">
      <RiskRegisterHeader />

      <FilterTabs
        selected={filter}
        onChange={setFilter}
        counts={counts}
        total={report.riskRegister.length}
      />

      {filtered.length === 0 ? (
        <div className="rreg-empty">No risks found for this filter.</div>
      ) : (
        <div className="rreg-list">
          {filtered.map((risk, i) => {
            const rid = risk.id || `idx-${i}`;
            return (
              <RiskRow
                key={rid}
                risk={risk}
                index={i}
                active={openId === rid}
                onOpen={() => setOpenId(rid)}
              />
            );
          })}
        </div>
      )}

      <div className="rreg-footer">
        <span>Showing {filtered.length} of {report.riskRegister.length} findings</span>
      </div>

      <div className="rovw-scope-band" style={{ marginTop: 16 }}>
        <span className="aq-icon" aria-hidden>info</span>
        <div className="rovw-scope-band-text">
          Risk+ identifies exposure; it does not verify safeguards. All ratings are inherent risk levels before mitigations.
        </div>
      </div>

      {openRisk && (
        <RiskDetailDrawer risk={openRisk} onClose={() => setOpenId(null)} />
      )}
    </div>
  );
}

const RiskPlusRiskFindings = () => {
  const { useCaseChatId = '' } = useParams<{ useCaseChatId: string }>();
  const data = useRiskPlusPageData(useCaseChatId);

  return (
    <RiskPlusPageShell
      useCaseChatId={useCaseChatId}
      data={data}
      secondaryNav={<RiskRegisterSecondaryNav useCaseChatId={useCaseChatId} />}
    >
      {(report) => <RiskRegisterContent report={report} />}
    </RiskPlusPageShell>
  );
};

export default RiskPlusRiskFindings;
